
import Vue from "vue";
import {
  CandidateAiAssessmentsViewOptions,
  AssessmentQuestion,
  Assessment
} from "@/interfaces/candidate/canidate_assessments";

export default Vue.extend({
  name: "AssessmentHeader",
  props: {
    assessment: {
      type: Object as () => Assessment | null,
      default: null
    },
    questions: {
      type: Array as () => AssessmentQuestion[],
      default: () => []
    },
    currentQuestionIndex: {
      type: Number,
      default: 0
    },
    timeRemaining: {
      type: Number,
      default: 0
    },
    activeQuestionType: {
      type: String,
      default: CandidateAiAssessmentsViewOptions.MULTIPLE_CHOICE
    },
    allQuestions: {
      type: Array as () => AssessmentQuestion[],
      default: () => []
    },
    answeredQuestionsCount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      activeTab: this.activeQuestionType,
      CandidateAiAssessmentsViewOptions
    };
  },
  computed: {
    progressPercentage(): number {
      if (!this.assessment?.total_questions) return 0;
      return (
        (this.currentQuestionIndex / this.assessment.total_questions) * 100
      );
    },
    availableQuestionTypes() {
      const types = [
        {
          value: CandidateAiAssessmentsViewOptions.MULTIPLE_CHOICE,
          label: this.$t("ai-assessments.header.multiple-choice"),
          icon: "mdi-checkbox-multiple-marked"
        },
        {
          value: CandidateAiAssessmentsViewOptions.SHORT_ANSWER,
          label: this.$t("ai-assessments.header.short-question"),
          icon: "mdi-text"
        },
        {
          value: CandidateAiAssessmentsViewOptions.TRUE_FALSE,
          label: this.$t("ai-assessments.header.true-false"),
          icon: "mdi-check-circle"
        }
      ];

      // Only show tabs for question types that exist in the assessment
      return types.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (type) => (this as any).getQuestionCount(type.value) > 0
      );
    },
    remainingQuestions(): number {
      const total = this.assessment?.total_questions || 0;
      const answered = this.answeredQuestionsCount || 0;
      const remaining = total - answered;
      return remaining > 0 ? remaining : 0;
    }
  },
  watch: {
    activeQuestionType(newType) {
      this.activeTab = newType;
    }
  },
  methods: {
    getDifficultyColor(difficulty: string): string {
      switch (difficulty?.toLowerCase()) {
        case "easy":
          return "success";
        case "medium":
          return "warning";
        case "hard":
          return "error";
        default:
          return "dark-light";
      }
    },
    getQuestionCount(questionType: CandidateAiAssessmentsViewOptions): number {
      return this.questions.filter((q) => q.question_type === questionType)
        .length;
    },
    switchQuestionType(questionType: CandidateAiAssessmentsViewOptions) {
      this.$emit("question-type-changed", questionType);
    },
    formatTime(seconds: number): string {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    },
    getCurrentQuestionNumber(): number {
      // Get the current question from the filtered questions
      const currentQuestion = this.questions[this.currentQuestionIndex];
      if (!currentQuestion || !this.allQuestions.length) {
        return this.answeredQuestionsCount + 1;
      }

      // Find the position of current question in the overall assessment
      const positionInAll = this.allQuestions.findIndex(
        (q) => q.id === currentQuestion.id
      );
      return positionInAll >= 0
        ? positionInAll + 1
        : this.answeredQuestionsCount + 1;
    }
  }
});
